import { Fragment, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import {
  BanknotesIcon,
  BriefcaseIcon,
  BuildingOfficeIcon,
  CheckBadgeIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  DocumentDuplicateIcon,
  FlagIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { TWclassNames } from "../../utils/Div";
import { PMDialog } from "../../utils/PMDialog";
import { fetchModuleTagMentions } from "../../../api/module_tags/mentions";
import { ResourceLoading } from "../../utils/ResourceLoading";

const getMentionUrl = (mentionItem) => {
  if (mentionItem?.type === "case") {
    return `/dashboard/cases/${mentionItem?.object?.id}`;
  }
  if (mentionItem?.type === "note") {
    switch (mentionItem?.module) {
      case "report":
        return `/dashboard/service/reports/${mentionItem?.module_param}`;
      case "work_order":
        return `/dashboard/work_orders/${mentionItem?.module_param}`;
      case "project":
        return `/dashboard/projects/${mentionItem?.module_param}`;
      case "customer":
        return `/dashboard/customers/${mentionItem?.module_param}`;
      case "contact":
        return `/dashboard/contacts?contactId=${mentionItem?.module_param}`;
      case "service_agreement":
        return `/dashboard/service/agreements/${mentionItem?.module_param}`;
      case "order":
        return `/dashboard/orders/${mentionItem?.module_param}`;
      case "product":
        return `/dashboard/products/${mentionItem?.module_param}`;
      case "folder":
        return `/dashboard/folders/${mentionItem?.module_param}`;
      default:
        return "/";
    }
  }
  return "/";
};

/**
 * Admin shortcuts for work order read view (desktop sidebar + mobile drawer).
 * Same UX pattern as ReportAdminMenu.
 */
export function WorkOrderAdminMenu({
  workOrder,
  hasAccess,
  onOpenApprove,
  onOpenBilling,
  onOpenFinish,
  onOpenAdminSetStatus,
  canApprove,
  canMarkReadyForInvoicing,
  canOpenLinkedSalesOrder,
  canEdit,
  canOpenFinish,
  canAdminSetStatus,
}) {
  const { t } = useTranslation();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mentionsDialogOpen, setMentionsDialogOpen] = useState(false);
  const [mentions, setMentions] = useState([]);
  const [mentionsLoading, setMentionsLoading] = useState(false);

  useEffect(() => {
    if (mentionsDialogOpen && workOrder?.id) {
      setMentionsLoading(true);
      fetchModuleTagMentions({ module: "work_order", id: workOrder.id })
        .then((res) => {
          setMentions(res?.data?.data || []);
        })
        .catch(() => setMentions([]))
        .finally(() => setMentionsLoading(false));
    }
  }, [mentionsDialogOpen, workOrder?.id]);

  const closeMobileDrawer = () => setMobileDrawerOpen(false);

  const runWithOptionalClose = (fn) => {
    return () => {
      fn();
      closeMobileDrawer();
    };
  };

  if (!hasAccess || !workOrder?.id) {
    return null;
  }

  const menuItems = [
    canEdit && {
      id: "edit",
      label: t("work_order.admin.edit"),
      description: t("work_order.admin.edit_desc"),
      icon: PencilSquareIcon,
      path: `/dashboard/work_orders/edit/${workOrder.id}`,
      color: "text-teal-600 hover:bg-teal-50",
    },
    canApprove && {
      id: "approve",
      label: t("work_order.admin.approve"),
      description: t("work_order.admin.approve_desc"),
      icon: ShieldCheckIcon,
      action: runWithOptionalClose(() => onOpenApprove?.()),
      color: "text-teal-700 hover:bg-teal-50",
    },
    workOrder?.project?.id && {
      id: "project",
      label: t("work_order.admin.open_project"),
      description: workOrder.project?.displayTitle,
      icon: BriefcaseIcon,
      path: `/dashboard/projects/${workOrder.project.id}`,
      color: "text-indigo-600 hover:bg-indigo-50",
    },
    workOrder?.customer?.id && {
      id: "customer",
      label: t("work_order.admin.open_customer"),
      description: workOrder.customer?.displayTitle || workOrder.customer?.name,
      icon: BuildingOfficeIcon,
      path: `/dashboard/customers/${workOrder.customer.id}`,
      color: "text-slate-600 hover:bg-slate-50",
    },
    {
      id: "item_consumption",
      label: t("work_order.admin.item_consumption"),
      description: t("work_order.admin.item_consumption_desc"),
      icon: CubeIcon,
      path: `/dashboard/work_orders/${workOrder.id}/item-consumption`,
      color: "text-teal-700 hover:bg-teal-50",
    },
    canOpenLinkedSalesOrder &&
      workOrder?.order?.id && {
        id: "sales_order",
        label: t("work_orders.billing_ready.open_order"),
        description: workOrder.order?.displayTitle,
        icon: ClipboardDocumentListIcon,
        path: `/dashboard/orders/${workOrder.order.id}`,
        color: "text-emerald-700 hover:bg-emerald-50",
      },
    canMarkReadyForInvoicing && {
      id: "billing",
      label: t("work_orders.billing_ready.action"),
      description: t("work_order.admin.billing_desc"),
      icon: BanknotesIcon,
      action: runWithOptionalClose(() => onOpenBilling?.()),
      color: "text-amber-800 hover:bg-amber-50",
    },
    canAdminSetStatus && {
      id: "admin_set_status",
      label: t("work_order.admin.set_status"),
      description: t("work_order.admin.set_status_desc"),
      icon: WrenchScrewdriverIcon,
      action: runWithOptionalClose(() => onOpenAdminSetStatus?.()),
      color: "text-slate-700 hover:bg-slate-50",
    },
    canOpenFinish && {
      id: "finish",
      label: t("work_order.admin.finish"),
      description: t("work_order.admin.finish_desc"),
      icon: CheckBadgeIcon,
      action: runWithOptionalClose(() => onOpenFinish?.()),
      color: "text-emerald-700 hover:bg-emerald-50",
    },
    {
      id: "mentions",
      label: t("work_order.admin.mentions"),
      description: t("work_order.admin.mentions_desc"),
      icon: DocumentDuplicateIcon,
      action: runWithOptionalClose(() => setMentionsDialogOpen(true)),
      color: "text-purple-600 hover:bg-purple-50",
    },
  ].filter(Boolean);

  const renderMenuRows = (opts = { closeOnNavigate: false }) => (
    <div className="divide-y divide-gray-100 p-2">
      {menuItems.map((item) => (
        <div key={item.id}>
          {item.path ? (
            <Link
              to={item.path}
              onClick={opts.closeOnNavigate ? closeMobileDrawer : undefined}
              className={TWclassNames(
                "flex items-start gap-3 rounded-lg px-3 py-3 transition-colors sm:py-2.5",
                item.color
              )}
            >
              <item.icon className="mt-0.5 h-6 w-6 flex-shrink-0 sm:h-5 sm:w-5" />
              <div className="min-w-0 flex-1">
                <p className="text-base font-medium sm:text-sm">{item.label}</p>
                {item.description && (
                  <p className="mt-0.5 truncate text-xs text-gray-500">
                    {item.description}
                  </p>
                )}
              </div>
              <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400 sm:h-4 sm:w-4" />
            </Link>
          ) : (
            <button
              type="button"
              onClick={item.action}
              className={TWclassNames(
                "flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left transition-colors sm:py-2.5",
                item.color
              )}
            >
              <item.icon className="mt-0.5 h-6 w-6 flex-shrink-0 sm:h-5 sm:w-5" />
              <div className="min-w-0 flex-1">
                <p className="text-base font-medium sm:text-sm">{item.label}</p>
                {item.description && (
                  <p className="mt-0.5 truncate text-xs text-gray-500">
                    {item.description}
                  </p>
                )}
              </div>
              <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400 sm:h-4 sm:w-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );

  const menuCardHeader = (
    <div className="border-b border-gray-100 px-4 py-3">
      <div className="flex items-center gap-2">
        <FlagIcon className="h-5 w-5 text-gray-500" />
        <h3 className="text-sm font-semibold text-gray-800">
          {t("work_order.admin.title")}
        </h3>
      </div>
    </div>
  );

  return (
    <>
      <div className="hidden lg:block">
        <div className="sticky top-4 rounded-xl border border-gray-200 bg-white shadow-sm">
          {menuCardHeader}
          {renderMenuRows({ closeOnNavigate: false })}
        </div>
      </div>

      {typeof document !== "undefined" &&
        createPortal(
          <>
            <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[45] flex justify-end px-4 pb-[max(4.75rem,calc(3.5rem+env(safe-area-inset-bottom)))] pt-2 lg:hidden">
              <button
                type="button"
                onClick={() => setMobileDrawerOpen(true)}
                className="pointer-events-auto flex h-14 w-14 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-800 shadow-lg ring-1 ring-black/5 transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                aria-label={t("work_order.admin.title")}
              >
                <FlagIcon className="h-7 w-7" aria-hidden />
              </button>
            </div>

            <Transition.Root show={mobileDrawerOpen} as={Fragment}>
              <Dialog
                as="div"
                className="relative z-[55] lg:hidden"
                onClose={setMobileDrawerOpen}
              >
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="fixed inset-0 bg-black/40" aria-hidden />
                </Transition.Child>

                <div className="fixed inset-0 overflow-hidden">
                  <div className="absolute inset-0 overflow-hidden">
                    <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                      <Transition.Child
                        as={Fragment}
                        enter="transform transition ease-out duration-300"
                        enterFrom="translate-x-full"
                        enterTo="translate-x-0"
                        leave="transform transition ease-in duration-200"
                        leaveFrom="translate-x-0"
                        leaveTo="translate-x-full"
                      >
                        <Dialog.Panel className="pointer-events-auto flex h-full w-screen max-w-md flex-col border-l border-gray-200 bg-white shadow-2xl">
                          <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 py-3">
                            <div className="flex items-center gap-2">
                              <FlagIcon className="h-5 w-5 text-gray-500" />
                              <Dialog.Title className="text-base font-semibold text-gray-900">
                                {t("work_order.admin.title")}
                              </Dialog.Title>
                            </div>
                            <button
                              type="button"
                              className="rounded-full p-2 text-gray-500 hover:bg-gray-100"
                              onClick={closeMobileDrawer}
                              aria-label={t("Close", { defaultValue: "Close" })}
                            >
                              <XMarkIcon className="h-6 w-6" aria-hidden />
                            </button>
                          </div>
                          <div className="min-h-0 flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">
                            {renderMenuRows({ closeOnNavigate: true })}
                          </div>
                        </Dialog.Panel>
                      </Transition.Child>
                    </div>
                  </div>
                </div>
              </Dialog>
            </Transition.Root>
          </>,
          document.body
        )}

      <PMDialog
        title={t("work_order.admin.mentions_dialog_title")}
        open={mentionsDialogOpen}
        onClose={() => setMentionsDialogOpen(false)}
      >
        {mentionsLoading ? (
          <ResourceLoading />
        ) : mentions?.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {mentions.map((mentionItem) => (
              <Link
                key={mentionItem.id}
                to={getMentionUrl(mentionItem)}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-3 hover:bg-gray-50"
              >
                <p className="font-medium text-gray-900">
                  {mentionItem?.type === "note"
                    ? `${t("note")} (${t(mentionItem?.module)})`
                    : t(mentionItem?.type)}
                </p>
                <p className="text-sm text-gray-500">
                  {mentionItem?.type === "note"
                    ? mentionItem?.object?.note
                    : mentionItem?.object?.displayTitle}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  {t("report.admin.mentioned_at")}{" "}
                  {new Date(mentionItem.created_at).toLocaleString("nb-NO")}
                </p>
              </Link>
            ))}
          </div>
        ) : (
          <p className="px-4 py-6 text-center text-sm text-gray-500">
            {t("work_order.admin.no_mentions")}
          </p>
        )}
      </PMDialog>
    </>
  );
}
